import { useEffect, useState, useCallback } from 'react';
import { api } from '../api';
import { useToast, Modal, ConfirmModal, useIsAdmin, formatDate } from '../components/shared';

const STATUS = {
  needed: { label: 'مطلوبة', badge: 'badge-yellow' },
  bought: { label: 'اتشرت', badge: 'badge-green' },
  cancelled: { label: 'اتلغت', badge: 'badge-gray' },
};
const EMPTY = { name: '', quantity: 1, client_id: '', device: '', buy_from: '', est_price: '', notes: '', due_date: '' };
const todayStr = () => new Date().toLocaleDateString('en-CA');
const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';

function BuyFrom({ value }) {
  if (!value) return <span className="text-dim">—</span>;
  if (/^https?:\/\//i.test(value)) {
    let host = value;
    try { host = new URL(value).hostname.replace(/^www\./, ''); } catch (e) {}
    return <a className="link" href={value} target="_blank" rel="noreferrer">🔗 {host}</a>;
  }
  const phone = value.replace(/\D/g, '');
  if (/^01\d{9}$/.test(phone)) return <a className="link" href={`https://wa.me/2${phone}`} target="_blank" rel="noreferrer">💬 {value}</a>;
  return <span>{value}</span>;
}

function NeedForm({ initial, clients, onSave, onClose }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const toast = useToast();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit() {
    if (!form.name) return toast('اسم القطعة مطلوب', 'error');
    try {
      await onSave({ ...form, quantity: Number(form.quantity) || 1, est_price: form.est_price === '' || form.est_price === null ? null : Number(form.est_price) });
    } catch (e) { toast(e.message, 'error'); }
  }

  return (
    <Modal title={initial.id ? 'تعديل' : 'احتياج جديد'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
        <button className="btn btn-primary btn-sm" onClick={submit}>حفظ</button>
      </>
    }>
      <div dir="rtl">
        <div className="grid-2">
          <div className="field"><label>القطعة *</label>
            <input className="input" value={form.name} onChange={set('name')} autoFocus /></div>
          <div className="field"><label>الكمية</label>
            <input className="input" type="number" min="1" value={form.quantity} onChange={set('quantity')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>العميل</label>
            <select className="select" value={form.client_id || ''} onChange={set('client_id')}>
              <option value="">—</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="field"><label>الجهاز</label>
            <input className="input" value={form.device || ''} onChange={set('device')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>مكان الشراء (اسم / رقم / لينك)</label>
            <input className="input" value={form.buy_from || ''} onChange={set('buy_from')} /></div>
          <div className="field"><label>السعر التقريبي</label>
            <input className="input" type="number" value={form.est_price ?? ''} onChange={set('est_price')} /></div>
        </div>
        <div className="field"><label>تاريخ الشراء (يظهر في الكاليندر وتيكتيك)</label>
          <input className="input" type="date" value={form.due_date || ''} onChange={set('due_date')} /></div>
        <div className="field"><label>ملاحظات</label>
          <textarea className="input" rows={2} value={form.notes || ''} onChange={set('notes')} /></div>
      </div>
    </Modal>
  );
}

export default function PartNeeds() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [quick, setQuick] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [statusF, setStatusF] = useState(['needed']);
  const [clientF, setClientF] = useState('');
  const [search, setSearch] = useState('');
  const toast = useToast();
  const isAdmin = useIsAdmin();

  const load = useCallback(() => { api.getPartNeeds().then(setItems).catch(e => toast(e.message, 'error')); }, [toast]);
  useEffect(() => { load(); api.getClients().then(setClients).catch(() => {}); }, [load]);

  async function quickAdd(e) {
    e && e.preventDefault();
    if (!quick.name.trim()) return;
    try {
      await api.createPartNeed({ ...quick, name: quick.name.trim(), quantity: Number(quick.quantity) || 1, est_price: quick.est_price === '' ? null : Number(quick.est_price) });
      setQuick(q => ({ ...EMPTY, client_id: q.client_id }));
      load();
    } catch (err) { toast(err.message, 'error'); }
  }

  async function setStatus(it, status) {
    setItems(list => list.map(x => x.id === it.id ? { ...x, status } : x));
    try { await api.updatePartNeed(it.id, { status }); load(); } catch (e) { toast(e.message, 'error'); load(); }
  }

  async function save(form) {
    await api.updatePartNeed(edit.id, form);
    toast('اتعدلت'); setEdit(null); load();
  }

  const filtered = items.filter(it => {
    if (statusF.length && !statusF.includes(it.status)) return false;
    if (clientF && it.client_id !== clientF) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![it.name, it.device, it.buy_from, it.client_name, it.notes].some(v => v && v.toLowerCase().includes(q))) return false;
    }
    return true;
  });
  const needed = items.filter(i => i.status === 'needed');
  const neededCost = needed.reduce((s, i) => s + (i.est_price || 0) * (i.quantity || 1), 0);
  const toggle = s => setStatusF(f => f.includes(s) ? f.filter(x => x !== s) : [...f, s]);
  const setQ = k => e => setQuick(q => ({ ...q, [k]: e.target.value }));

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">PARTS NEEDED <span>_</span></h1>
        <div className="text-sm text-muted" dir="rtl">مطلوب: <b>{needed.length}</b> · تكلفة تقريبية: <b>{money(neededCost)}</b></div>
      </div>

      {isAdmin && (
        <form onSubmit={quickAdd} className="card mb-16" style={{ padding: 12 }} dir="rtl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, alignItems: 'end' }}>
            <input className="input" style={{ gridColumn: 'span 2' }} placeholder="القطعة اللي محتاجها *" value={quick.name} onChange={setQ('name')} />
            <input className="input" type="number" min="1" placeholder="الكمية" value={quick.quantity} onChange={setQ('quantity')} />
            <select className="select" value={quick.client_id} onChange={setQ('client_id')}>
              <option value="">العميل —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input className="input" placeholder="الجهاز" value={quick.device} onChange={setQ('device')} />
            <input className="input" placeholder="مكان الشراء / لينك" value={quick.buy_from} onChange={setQ('buy_from')} />
            <input className="input" type="number" placeholder="السعر" value={quick.est_price} onChange={setQ('est_price')} />
            <input className="input" type="date" title="تاريخ الشراء" value={quick.due_date} onChange={setQ('due_date')} />
            <button className="btn btn-primary" type="submit">+ إضافة</button>
          </div>
        </form>
      )}

      <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', alignItems: 'center' }} dir="rtl">
        <input className="input" style={{ maxWidth: 220 }} placeholder="🔍 بحث..." value={search} onChange={e => setSearch(e.target.value)} />
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} className={`btn btn-sm ${statusF.includes(k) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggle(k)}>{v.label}</button>
        ))}
        <select className="select" style={{ width: 'auto' }} value={clientF} onChange={e => setClientF(e.target.value)}>
          <option value="">كل العملاء</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="table-wrap" dir="rtl">
        <table>
          <thead>
            <tr><th>القطعة</th><th>الكمية</th><th>العميل</th><th>الجهاز</th><th>مكان الشراء</th><th>السعر</th><th>التاريخ</th><th>الحالة</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9}><div className="empty">لا يوجد</div></td></tr>
            ) : filtered.map(it => (
              <tr key={it.id} style={it.status === 'cancelled' ? { opacity: 0.5 } : {}}>
                <td className="fw-600">{it.name}{it.ticktick_id && <span className="text-xs" title="TickTick" style={{ color: 'var(--primary)', marginInlineStart: 6 }}>✓TT</span>}{it.notes && <div className="text-xs text-dim">{it.notes}</div>}</td>
                <td className="mono">{it.quantity}</td>
                <td className="text-sm">{it.client_name || <span className="text-dim">—</span>}</td>
                <td className="text-sm text-muted">{it.device || '—'}</td>
                <td className="text-sm"><BuyFrom value={it.buy_from} /></td>
                <td className="mono text-sm">{money(it.est_price)}</td>
                <td className="text-sm">
                  {it.due_date
                    ? <span className={it.status === 'needed' && it.due_date < todayStr() ? 'text-red fw-600' : it.due_date === todayStr() ? 'text-yellow fw-600' : ''}>{formatDate(it.due_date)}</span>
                    : isAdmin && it.status === 'needed'
                      ? <input type="date" className="input" style={{ padding: '2px 6px', width: 'auto' }} onChange={e => e.target.value && api.updatePartNeed(it.id, { due_date: e.target.value }).then(load)} />
                      : <span className="text-dim">—</span>}
                </td>
                <td><span className={`badge ${STATUS[it.status]?.badge || 'badge-gray'}`}>{STATUS[it.status]?.label || it.status}</span></td>
                <td>
                  {isAdmin && <div className="flex gap-8">
                    {it.status === 'needed'
                      ? <button className="btn btn-primary btn-sm" onClick={() => setStatus(it, 'bought')}>✓ اتشرت</button>
                      : <button className="btn btn-ghost btn-sm" onClick={() => setStatus(it, 'needed')}>↩</button>}
                    <button className="btn btn-ghost btn-sm" onClick={() => setEdit(it)}>تعديل</button>
                    {it.status === 'needed' && <button className="btn btn-ghost btn-sm" onClick={() => setStatus(it, 'cancelled')}>إلغاء</button>}
                    <button className="btn btn-danger btn-sm" onClick={() => setConfirm(it.id)}>حذف</button>
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && <NeedForm initial={edit} clients={clients} onSave={save} onClose={() => setEdit(null)} />}
      {confirm && <ConfirmModal message="حذف القطعة؟" onConfirm={async () => { await api.deletePartNeed(confirm); setConfirm(null); load(); }} onClose={() => setConfirm(null)} />}
    </div>
  );
}
